import React, { useLayoutEffect, useRef, useState } from 'react';
import { FLOOR_CANVAS, fitScale } from '../../utils/floorPlan';

/**
 * Ο καμβάς της σάλας, κοινός για editor κάτοψης και ταμείο.
 *
 * Τα παιδιά ζωγραφίζονται σε ΣΤΑΘΕΡΟ λογικό καμβά (FLOOR_CANVAS, σε px) και όλο το
 * επίπεδο κλιμακώνεται με transform ώστε να χωρέσει στο διαθέσιμο πλαίσιο. Θέσεις
 * (%), μεγέθη τραπεζιών (px) και γράμματα αλλάζουν μαζί — η σάλα δείχνει ίδια σε
 * οθόνη 24'' και σε ταμείο 1024×768, απλώς μικρότερη.
 *
 * Το ύψος είναι ό,τι απομένει ως το κάτω μέρος της οθόνης. Δεν γίνεται με
 * calc(100vh - …): η εφαρμογή βάζει zoom στη ρίζα σε μικρές οθόνες και το vh δεν
 * το λογαριάζει, οπότε ο καμβάς έβγαινε το ¾ του διαθέσιμου.
 *
 * `innerRef` δείχνει στο κλιμακωμένο επίπεδο: το getBoundingClientRect του δίνει
 * το ΟΡΑΤΟ μέγεθος, που χρειάζεται για να γίνουν pixels του ποντικιού ποσοστά.
 */
interface Props {
  /** Κενό (σε px οθόνης) κάτω από τον καμβά. */
  bottomGap?: number;
  minHeight?: number;
  /** Όψη του καμβά (φόντο, περίγραμμα) — ζωγραφίζεται κι αυτή σε λογικά px. */
  className?: string;
  innerRef?: React.Ref<HTMLDivElement>;
  onPointerDown?: (e: React.PointerEvent<HTMLDivElement>) => void;
  innerStyle?: React.CSSProperties;
  /** Περιεχόμενο πάνω από τον καμβά σε κανονικό μέγεθος (π.χ. «δεν υπάρχουν τραπέζια»). */
  overlay?: React.ReactNode;
  /** Λογικός καμβάς (προεπιλογή 1000×600)· η ζώνη με διάταξη από τη Sala έχει δικό της ύψος. */
  canvas?: { width: number; height: number };
  children?: React.ReactNode;
}

const FloorCanvas: React.FC<Props> = ({
  bottomGap = 16, minHeight = 300, className = '', innerRef, onPointerDown, innerStyle, overlay, canvas = FLOOR_CANVAS, children
}) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState({ height: minHeight, scale: 0 });

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const measure = () => {
      const rect = box.getBoundingClientRect();
      // rect σε px οθόνης, offsetWidth σε px CSS: ο λόγος τους είναι το zoom της ρίζας.
      const zoom = box.offsetWidth > 0 && rect.width > 0 ? rect.width / box.offsetWidth : 1;
      const height = Math.max(minHeight, (window.innerHeight - rect.top - bottomGap) / zoom);
      const scale = fitScale({ width: box.clientWidth, height }, canvas);
      setLayout(prev => (Math.abs(prev.height - height) < 0.5 && Math.abs(prev.scale - scale) < 0.001 ? prev : { height, scale }));
    };
    measure();
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(box);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [bottomGap, minHeight, canvas.width, canvas.height]);

  const { height, scale } = layout;
  return (
    <div ref={boxRef} className="relative w-full flex justify-center items-start" style={{ height }}>
      {scale > 0 && (
        <div className="relative shrink-0" style={{ width: canvas.width * scale, height: canvas.height * scale }}>
          <div
            ref={innerRef}
            onPointerDown={onPointerDown}
            className={`absolute left-0 top-0 ${className}`}
            style={{
              width: canvas.width,
              height: canvas.height,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              ...innerStyle
            }}
          >
            {children}
          </div>
          {overlay && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="pointer-events-auto">{overlay}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FloorCanvas;
